// Игровое поле: барабаны поверх фона, всё по центру экрана.
//
// Поле собирает вместе барабаны, кнопки и рамку машины, а сам спин ведёт game/spinRound.ts.
// Бонусный ряд закрыт створками до запуска бонуса, поле 3x3 открывается на старте.
//
// Машина живёт одним узлом: её картинка, дым под ней и всё, что на ней лежит. Двигает его
// взгляд игры, см. ui/camera.ts. Фон и кнопки в узел не входят: они стоят на экране,
// а не на машине.

import { Container, Sprite } from 'pixi.js';

import type { Cents, GameConfig } from '../api/gameConfig';
import type { SessionSnapshot } from '../api/session';
import { requestSpin, type Cheat, type RoundResult } from '../api/spin';
import { REELS_GRID, REELS_IMAGE, RUSH_SMOKE_SKELETON, SMOKE_SKELETON, UI_SHEET } from '../assets/catalog';
import type { AssetManager } from '../assets/manager';
import { createSteamSpin, type SteamSpin } from '../features/steamSpin/steamSpin';
import { installConsole, removeConsole } from '../game/console';
import { playCheat, type CheatBoard } from '../game/cheats';
import type { GameState } from '../game/game';
import { SpinRounds } from '../game/spinRound';
import type { StateMachine } from '../game/stateMachine';
import type { SymbolLibrary } from '../game/symbols';
import { Wallet } from '../game/wallet';
import type { Locale } from '../i18n/locale';
import { listenTranslation, text } from '../i18n/text';
import { createAnticipation, type Anticipation } from '../ui/anticipation';
import { AutoplayButton } from '../ui/autoplayButton';
import { ARRIVAL_SECONDS, Arrival } from '../ui/arrival';
import { Background } from '../ui/background';
import { BigWin } from '../ui/bigWin';
import { BottomBar } from '../ui/bottomBar';
import { Camera } from '../ui/camera';
import { FieldCover } from '../ui/fieldCover';
import { FullscreenButton } from '../ui/fullscreenButton';
import { Help } from '../ui/help';
import { ANCHOR_X, ANCHOR_Y, BONUS_ROW, FIELD_VIEW, INTRO_VIEW, MAIN_ROW, VIEW_BOTTOM, VIEW_TOP, dividers, onImage } from '../ui/machine';
import { Notice } from '../ui/notice';
import { ReelsView } from '../ui/reels';
import { Ride } from '../ui/ride';
import { gameLayout } from '../ui/layout';
import type { Scene, Viewport } from '../ui/scene';
import { Smoke } from '../ui/smoke';
import { SoundButton } from '../ui/soundButton';
import { Steam } from '../ui/steam';
import { SpinControls } from '../ui/spinControls';
import { Settings } from '../ui/settings';
import { WinShow } from '../ui/winShow';

/** Заглушка поля 3x3: игра начинается с её открытия, а дальше она объявляет фичи. */
function fieldCover(assets: AssetManager): FieldCover {
  const center = onImage(REELS_GRID.centerX, MAIN_ROW.centerY);

  return new FieldCover(assets, center.x, center.y);
}

/** Игровое поле. */
export class GameScene implements Scene {
  readonly view: Container = new Container();

  readonly #background: Background;

  /** Узел фона. Под видимую область фон растягивается сам, а приближает его взгляд. */
  readonly #backdrop: Container = new Container();

  /** Общий масштаб поля и фона в мобильной раскладке. */
  readonly #world: Container = new Container();

  /** Узел машины: её картинка, дым под ней и всё, что на ней лежит. Его и двигает взгляд. */
  readonly #machine: Container = new Container();

  /**
   * Узел подъезда внутри узла машины.
   *
   * Подъезд и взгляд водят машину каждый по-своему и разом: взгляд ходит на бонусе, подъезд
   * идёт в начале игры. Общий узел они делили бы, затирая друг друга, поэтому у каждого свой.
   */
  readonly #approach: Container = new Container();

  /**
   * Узел интерфейса: полоса со счётом, кнопка спина и кнопки помощи.
   *
   * Одним узлом они проявляются разом, пока машина подъезжает. Порознь каждый набирал бы
   * прозрачность сам, и три ползущих числа разошлись бы на первой же правке.
   */
  readonly #panel: Container = new Container();

  /**
   * Слой попапов над всеми кнопками игры.
   *
   * Затемнение попапа накрывает то, что лежит под ним, а кнопки разложены по своим узлам рядом
   * с ним. Держи попап его хозяин, накрыл бы он только те узлы, которые сцена добавила раньше:
   * таблица выплат оставила бы гореть поверх затемнения звук, настройки и полный экран.
   */
  readonly #popups: Container = new Container();

  /** Сколько интерфейс уже проявляется. Полную прозрачность он набирает вместе с приездом машины. */
  #shown: number = 0;

  readonly #reels: ReelsView;

  /** Пар под голос стоящей машины: её приезд и гудок за выигрыш. */
  readonly #smoke: Smoke;

  /** Пар на полном ходу. Он идёт всю поездку бонуса и с гудком не смешивается. */
  readonly #rushSmoke: Smoke;

  /** Пар у манометров: струя из крана и завеса под ними на время бонуса. */
  readonly #steam: Steam;

  readonly #wins: WinShow;

  /** Анимация ожидания. Null — скаттера или бонуса за скаттеры в слоте нет. */
  readonly #anticipation: Anticipation | null;

  readonly #cover: FieldCover;
  readonly #camera: Camera;
  readonly #ride: Ride;
  readonly #arrival: Arrival;
  readonly #controls: SpinControls;
  readonly #autoplay: AutoplayButton;
  readonly #bigWin: BigWin;
  readonly #notice: Notice;
  readonly #rounds: SpinRounds;
  readonly #wallet: Wallet;
  readonly #bar: BottomBar;
  #help: Help;
  readonly #fullscreen: FullscreenButton;
  readonly #sound: SoundButton;
  #settings: Settings;
  readonly #stopTranslation: () => void;

  /** Фича Steam Spin. Null — в этом слоте её нет, и поле идёт без мини-слота и манометров. */
  readonly #steamSpin: SteamSpin | null;

  /** Заказ на следующий раунд из консоли тестировщика. Null — раунд идёт своим чередом. */
  #cheat: Cheat | null = null;

  /** Поле должно сначала приехать и открыться: до этого даже активный autoplay не крутит. */
  #spinsReady = false;

  /**
   * @param session Сессия игрока: её состояние ставит фичу в то положение, где её оставили
   * прошлые раунды, а её номер идёт в каждый запрос спина.
   */
  constructor(
    config: GameConfig,
    symbols: SymbolLibrary,
    assets: AssetManager,
    state: StateMachine<GameState>,
    session: SessionSnapshot,
    onLocaleChange: (locale: Locale) => void = () => {},
  ) {
    this.#background = new Background(assets);

    // Картинка барабанов лежит в квадрате с пустыми полями, поэтому якорь стоит
    // на самой машине, а не в центре файла. Так машина встаёт по центру макета,
    // и путь на фоне идёт ровно под ней.
    const reels = assets.texture(REELS_IMAGE);
    const picture = new Sprite(reels);
    picture.anchor.set(ANCHOR_X / reels.width, ANCHOR_Y / reels.height);

    // В макет по высоте вписана видимая полоса, а не вся машина: окна занимают всё место
    // над интерфейсом, а труба и тележка уходят за края экрана.
    // По ширине машина у́же макета с большим запасом, поэтому масштаб считается по высоте.
    const scale: number = FIELD_VIEW.height / (VIEW_BOTTOM - VIEW_TOP);
    picture.scale.set(scale);
    picture.position.set(0, FIELD_VIEW.centerY);

    // Всё, что лежит на машине, живёт в координатах её картинки: слой повторяет её масштаб
    // и стоит на том же якоре, поэтому внутри достаточно перевести точку через onImage.
    const layer = new Container();
    layer.scale.set(scale);
    layer.position.set(0, FIELD_VIEW.centerY);

    // Барабаны считают свои координаты прямо по сетке окон, поэтому их узел сдвинут
    // на якорь целиком, а не каждый символ по отдельности.
    this.#reels = new ReelsView(config, symbols);
    this.#reels.view.position.set(-ANCHOR_X, -ANCHOR_Y);

    // Дым идёт из-под машины, поэтому он лежит между фоном и её картинкой, а не в общем слое.
    // Масштаб и якорь он повторяет за слоем: внутри он считает координаты по картинке барабанов.
    //
    // Клубов два, и стоят они на одном месте: тихий пар стоящей машины и густой пар на ходу.
    // Разом они не идут — машина или стоит у станции, или едет, см. ADR-51.
    this.#smoke = new Smoke(assets, SMOKE_SKELETON);
    this.#rushSmoke = new Smoke(assets, RUSH_SMOKE_SKELETON);

    for (const smoke of [this.#smoke, this.#rushSmoke]) {
      smoke.view.scale.set(scale);
      smoke.view.position.set(-ANCHOR_X * scale, -ANCHOR_Y * scale + FIELD_VIEW.centerY);
    }

    // Показ выигрыша лежит поверх всего поля: дорожка линии идёт по разделителям барабанов,
    // а сумма выигрыша встаёт над символами.
    //
    // Отклик на выигрыш — гудок паровоза и клуб пара под ним: врозь они не идут, см. ADR-49.
    this.#wins = new WinShow(config, symbols, assets, this.#reels, {
      start: (): void => this.#smoke.start('trainWhistle'),
      stop: (): void => this.#smoke.stop(),
    });
    this.#wins.view.position.set(-ANCHOR_X, -ANCHOR_Y);

    // Рамка ожидания обхватывает барабан снаружи окна: она ложится на разделители и на рамку
    // машины, поэтому маску поля не делит и живёт своим узлом.
    this.#anticipation = createAnticipation(config, assets);
    this.#anticipation?.view.position.set(-ANCHOR_X, -ANCHOR_Y);

    // Заглушка лежит выше показа выигрыша: она закрывает поле целиком, а не его часть.
    this.#cover = fieldCover(assets);

    // Пар у манометров живёт в тех же координатах картинки барабанов, что и всё на машине.
    this.#steam = new Steam(assets);
    this.#steam.view.position.set(-ANCHOR_X, -ANCHOR_Y);

    // Взгляд двигает узел машины целиком, а вместе с ним фон: без этого машина росла бы
    // на неподвижной картинке и будто прыгала к игроку. Кнопки в ход взгляда не входят:
    // они стоят на экране, а не в мире игры.
    this.#backdrop.addChild(this.#background.view);
    this.#camera = new Camera(this.#machine, this.#backdrop, { anchorY: ANCHOR_Y, scale, centerY: FIELD_VIEW.centerY });

    // Поездка ведёт фон и пар как одно движение: земля идёт мимо машины, и из-под котла
    // бьёт пар, см. ADR-51. Взгляд игры к ней не привязан и ходит своим чередом.
    this.#ride = new Ride(this.#background, this.#rushSmoke);

    // Фича подключается описанием игры, а не встроена в поле: слот без неё её и не заводит.
    // Её узлы живут в тех же координатах картинки барабанов, что и всё на машине.
    this.#steamSpin = createSteamSpin({
      config,
      session: session.state,
      symbols,
      assets,
      field: this.#reels,
      cover: this.#cover,
      camera: this.#camera,
      ride: this.#ride,
      steam: this.#steam,
    });

    // Слои идут снизу вверх: барабаны поля, значения множителей поверх их символов, пар
    // у манометров, рамка машины с мини-слотом, манометрами и счётчиком, рамка ожидания,
    // показ выигрыша и заглушка поля.
    layer.addChild(this.#reels.view);

    if (this.#steamSpin !== null) {
      for (const view of [this.#steamSpin.bonusView, this.#steamSpin.gaugesView, this.#steamSpin.totalView, this.#steamSpin.multipliersView])
        view.position.set(-ANCHOR_X, -ANCHOR_Y);

      layer.addChild(this.#steamSpin.multipliersView);
    }

    layer.addChild(...dividers(config.reels.length, MAIN_ROW, assets), ...dividers(config.reels.length, BONUS_ROW, assets));

    // Пар лежит на рамке машины, но ниже стрелок манометров и мини-слота. Под циферблаты
    // его кладёт не слой, а высота: нарисованы они на самой картинке машины, см. ui/steam.ts.
    layer.addChild(this.#steam.view);

    if (this.#steamSpin !== null)
      layer.addChild(this.#steamSpin.bonusView, this.#steamSpin.gaugesView, this.#steamSpin.totalView);

    if (this.#anticipation !== null)
      layer.addChild(this.#anticipation.view);

    layer.addChild(this.#wins.view, this.#cover.view);

    // Дым идёт из-под машины, поэтому в узле он лежит ниже её картинки.
    this.#approach.addChild(this.#smoke.view, this.#rushSmoke.view, picture, layer);
    this.#machine.addChild(this.#approach);

    // Машина приходит с того места, где её оставил экран интро, и с ней приходит её пар.
    // Пар идёт под гудок тормозящего паровоза: это одно движение, см. ADR-49.
    this.#arrival = new Arrival(this.#approach, FIELD_VIEW.centerY, INTRO_VIEW.centerY, INTRO_VIEW.height / FIELD_VIEW.height);
    this.#smoke.start('trainArrives');

    this.#controls = new SpinControls(assets.sheet(UI_SHEET), () => {
      this.#rounds.spin();
    });

    this.#autoplay = new AutoplayButton(assets.sheet(UI_SHEET), () => this.#tryAutoplay(state));

    // Пока машина едет, крутить нечего: поле под закрытыми створками, и кнопка спина погашена.
    this.#controls.showSpin(false);

    void this.#arrival.finished.then((): void => {
      void this.#cover.release();
      this.#spinsReady = true;
      this.#controls.showSpin(this.#wallet.affords);
      this.#tryAutoplay(state);
    });

    // Интерфейс проявляется, пока машина едет, и встаёт в полную силу вместе с ней.
    // Ждать её приезда ему нечего: ожидание игрока это не сократило бы, а удлинило.
    this.#panel.alpha = 0;

    // Крупный выигрыш встаёт поверх всей игры со своим затемнением и уходит сам, см. ADR-56.
    this.#bigWin = new BigWin(assets);

    // Сообщение об ошибке лежит выше всего: пока оно на экране, игра под ним не работает.
    this.#notice = new Notice(assets.sheet(UI_SHEET), text('retry'));

    // Кошелёк держит счёт и ставку, а полоса под машиной их показывает. Счёт считает бэкенд,
    // клиент только берёт присланное, см. ADR-09.
    this.#wallet = new Wallet(config, session.state.balance, session.state.stake);
    this.#bar = new BottomBar(assets, this.#wallet);

    // Таблица выплат и правила игры: их открывают две кнопки в левом нижнем углу.
    this.#help = new Help(config, symbols, assets, this.#wallet, () => this.#settings.close());

    // Полный экран игрок берёт третьей кнопкой того же левого ряда, см. ADR-53.
    this.#fullscreen = new FullscreenButton(assets);

    // Звук игрок глушит кнопкой в правом верхнем углу, см. ADR-47.
    this.#sound = new SoundButton(assets.sheet(UI_SHEET));
    const cheatBoard: CheatBoard = {
      arm: (cheat: Cheat | null): void => {
        this.#cheat = cheat;
      },
      spin: (): void => {
        this.#rounds.spin();
      },
    };
    this.#settings = new Settings(assets, () => this.#help.close(), (locale) => {
      onLocaleChange(locale);
    }, session.demoCheats ? (cheat: Cheat) => playCheat(cheatBoard, cheat) : null);

    this.#stopTranslation = listenTranslation(() => this.#refreshText());

    this.#rounds = new SpinRounds({
      machine: state,
      reels: this.#reels,
      controls: this.#controls,
      wins: this.#wins,
      cover: this.#cover,
      notice: this.#notice,
      feature: this.#steamSpin,
      anticipation: this.#anticipation,
      bigWin: this.#bigWin,
      wallet: this.#wallet,
      bigWinRate: config.bigWinRate ?? null,
      ask: (round: string, bet: Cents): Promise<RoundResult> => {
        // Заказ живёт один запрос. Сорвался он или нет, повтор идёт обычным спином: раунд
        // повторяет запрос сам, пока не получит итог, и висеть на отказе ему нельзя.
        const ordered: Cheat | null = this.#cheat;
        this.#cheat = null;

        return requestSpin(round, session.id, bet, ordered ?? undefined);
      },
      onSpinAvailable: () => this.#tryAutoplay(state),
    }, () => this.#tryAutoplay(state));

    // Ставку игрок двигает между раундами: на ходу раунд её уже оплатил. Кнопка спина гаснет
    // вместе с выбором, когда счёта не хватает даже на самую дешёвую ставку.
    state.onChange((next: GameState): void => {
      const idle: boolean = next === 'idle';

      this.#bar.open = idle;

      if (idle) {
        this.#controls.showSpin(this.#wallet.affords);
        this.#tryAutoplay(state);
      }
    });

    // Смена ставки меняет и то, хватает ли на неё счёта.
    this.#wallet.listen(() => {
      if (state.state === 'idle') {
        this.#controls.showSpin(this.#wallet.affords);
        this.#tryAutoplay(state);
      }
    });

    // Консоль игры: заказ раунда выбирает тестировщик из консоли браузера, а играет его бэкенд.
    installConsole(cheatBoard);

    // Полоса и кнопки лежат поверх машины: они стоят в низу макета и в барабаны не заходят.
    // Попапы лежат над всеми кнопками разом, а сообщение об ошибке — выше всего: под ним игра
    // не работает. Оно и в узел интерфейса не входит: поломку игрок должен увидеть сразу,
    // а не по проявлении. Там же, вне узла интерфейса, лежит и крупный выигрыш: он накрывает
    // игру целиком и уходит сам.
    this.#popups.addChild(...this.#help.popups, ...this.#settings.popups);
    this.#panel.addChild(this.#bar.view, this.#controls.view, this.#autoplay.view, this.#help.view, this.#fullscreen.view, this.#sound.view, this.#settings.view, this.#popups);
    this.#world.addChild(this.#backdrop, this.#machine);
    this.view.addChild(this.#world, this.#panel, this.#bigWin.view, this.#notice.view);
  }

  layout(viewport: Viewport): void {
    const layout = gameLayout(viewport);
    const mobile: boolean = layout.composition === 'mobile';
    const worldScale: number = mobile ? Math.min(1.8, Math.max(1, viewport.height * 0.43 / FIELD_VIEW.height)) : 1;
    const fieldY: number = mobile ? -viewport.height * 0.11 : 0;
    this.#world.scale.set(worldScale);
    this.#world.position.set(0, fieldY - FIELD_VIEW.centerY * worldScale);

    this.#background.layout(viewport);
    this.#bar.layout(layout);
    this.#controls.layout(layout);
    this.#autoplay.layout(layout);
    this.#help.layout(layout);
    this.#fullscreen.layout(layout);
    this.#sound.layout(layout);
    this.#settings.layout(layout);
    this.#bigWin.layout(viewport);
    this.#notice.layout(viewport);
  }

  update(seconds: number): void {
    this.#background.update(seconds);
    this.#arrival.update(seconds);
    this.#showPanel(seconds);
    this.#reels.update(seconds);
    this.#steamSpin?.update(seconds);
    this.#anticipation?.update(seconds);
    this.#wins.update(seconds);
    this.#smoke.update(seconds);
    this.#rushSmoke.update(seconds);
    this.#steam.update(seconds);
    this.#ride.update(seconds);
    this.#cover.update(seconds);
    this.#camera.update(seconds);
    this.#bar.update(seconds);
    this.#controls.update(seconds);
    this.#autoplay.update(seconds);
    this.#help.update(seconds);
    this.#fullscreen.update(seconds);
    this.#sound.update(seconds);
    this.#settings.update(seconds);
    this.#bigWin.update(seconds);
    this.#notice.update(seconds);
  }

  /** Проявляет интерфейс, пока машина едет. Проявившийся трогать нечем. */
  #showPanel(seconds: number): void {
    if (this.#panel.alpha >= 1)
      return;

    this.#shown += seconds;
    this.#panel.alpha = Math.min(this.#shown / ARRIVAL_SECONDS, 1);
  }

  destroy(): void {
    removeConsole();
    this.#stopTranslation();
    this.#controls.destroy();
    this.#help.destroy();
    this.#settings.destroy();
    this.view.destroy({ children: true });
  }

  /** Обновляет подписи на месте, не трогая идущий раунд и дерево интерфейса. */
  #refreshText(): void {
    this.#bar.refreshText();
    this.#notice.buttonLabel = text('retry');
    this.#help.refreshText();
    this.#settings.refreshText();
  }

  /** Включённый режим не торопит текущий раунд: он начинает только следующий доступный. */
  #tryAutoplay(state: StateMachine<GameState>): void {
    const canSpin: boolean = state.state === 'idle' || state.state === 'result';

    if (this.#autoplay.active && this.#spinsReady && canSpin && this.#wallet.affords)
      this.#rounds.spin();
  }
}
